import React, { useEffect, useMemo, useState } from 'react';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import { useTranslation } from 'react-i18next';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import {
  chartBarSVG,
  chatSquareSVG,
  plusSignSVG,
  trendLineSVG,
} from '../../../lib/allCustomSVGs';
import SettingsIconSelectInput from '../../../ui/inputs/SettingsIconSelectInput';

// toggle/enable APIs
import update_custom_graph_notification_api_call from '../../../lib/api/Reports/notifications/update_custom_graph_notification_api_call';
import update_custom_prompt_notification_api_call from '../../../lib/api/Reports/notifications/update_custom_prompt_notification_api_call';

import { GraphApi } from '../../../lib/api/Reports/report_graph_api_call';
import { CustomPrompt } from '../../../interfaces/CustomPrompt';
import { add_shop_insight_api_call } from '../../../lib/api/Reports/notifications/shop_insights_api_calls';

type Props = {
  togglePopupModal: (isOpen: boolean) => void;
  onNotificationAdded?: () => void;

  // Preloaded & already filtered in ReportsNotifications
  integrationsAndMetrics: Array<{
    id: string; // slug
    mainTitle: string;
    mainIcon?: string;
    items: Array<{
      id?: string | number;
      title: string;
      value?: string;
      icon?: any;
      type?: 'insight';
    }>;
  }>;
  graphs: GraphApi[];
  prompts: CustomPrompt[];
};

const AddReportsNotifications = ({
  togglePopupModal,
  onNotificationAdded,
  integrationsAndMetrics,
  graphs,
  prompts,
}: Props) => {
  const { t } = useTranslation();

  // required types
  const [itemType, setItemType] = useState<'insight' | 'prompt' | 'graph'>(
    'insight'
  );

  // insight state
  const [integration, setIntegration] = useState(''); // slug
  const [metricsSelected, setMetricsSelected] = useState<string[]>(['']); // short-codes

  // prompt state
  const [customPromptId, setCustomPromptId] = useState(''); // UUID

  // graph state
  const [graphId, setGraphId] = useState(''); // UUID

  const [isSubmitting, setIsSubmitting] = useState(false);

  // -------- Integration (insights) options --------
  const integrationOptions = useMemo(
    () =>
      (integrationsAndMetrics || []).map((itg) => ({
        text: itg.mainTitle,
        value: itg.id,
        imageIcon: itg.mainIcon,
      })),
    [integrationsAndMetrics]
  );

  const baseMetricOptions = useMemo(() => {
    const sel = integrationsAndMetrics.find((i) => i.id === integration);
    if (!sel) return [];
    return (sel.items || []).map((m) => ({
      text: m.title,
      value: m.value ?? m.title, // short-code as value
      icon: m.icon ?? null,
    }));
  }, [integration, integrationsAndMetrics]);

  // reset metrics when integration changes
  useEffect(() => {
    setMetricsSelected(['']);
  }, [integration]);

  const handleMetricChange = (idx: number, val: string) => {
    setMetricsSelected((prev) => {
      const next = [...prev];
      next[idx] = val;
      return next;
    });
  };

  // filtered options per metric select (also prevents picking same metric twice)
  const getOptionsForMetricIndex = (index: number) => {
    const chosen = new Set(metricsSelected.filter(Boolean));
    const currentVal = metricsSelected[index];
    if (currentVal) chosen.delete(currentVal);
    return baseMetricOptions.filter((opt) => !chosen.has(opt.value));
  };

  // Build options for (already filtered) prompts/graphs
  const graphOptions = useMemo(
    () => (graphs || []).map((g) => ({ text: g.name, value: g.id })),
    [graphs]
  );
  const promptOptions = useMemo(
    () => (prompts || []).map((p) => ({ text: p.name, value: p.id })),
    [prompts]
  );

  // -------- Save handler --------
  const isSaveDisabled = useMemo(() => {
    if (itemType === 'insight') {
      const valid = integration && metricsSelected.filter(Boolean).length > 0;
      return !valid || isSubmitting;
    }
    if (itemType === 'graph') return !graphId || isSubmitting;
    if (itemType === 'prompt') return !customPromptId || isSubmitting;
    return true;
  }, [
    itemType,
    integration,
    metricsSelected,
    graphId,
    customPromptId,
    isSubmitting,
  ]);

  const handleSave = async () => {
    if (isSaveDisabled) return;
    setIsSubmitting(true);
    try {
      if (itemType === 'insight') {
        const sel = integrationsAndMetrics.find((i) => i.id === integration);
        const itemsByShort = new Map(
          (sel?.items || []).map((m) => [m.value ?? m.title, m])
        );

        for (const sc of metricsSelected.filter(Boolean)) {
          const meta = itemsByShort.get(sc);
          const insightId = meta?.id; // backend "insight_id"
          if (!insightId) continue;
          await add_shop_insight_api_call(insightId);
        }
      } else if (itemType === 'graph') {
        await update_custom_graph_notification_api_call(graphId, true);
      } else if (itemType === 'prompt') {
        await update_custom_prompt_notification_api_call(customPromptId, true);
      }
      onNotificationAdded?.();
      togglePopupModal(false);
    } catch (err) {
      console.error('Error creating notification item:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // -------- UI --------
  const renderInputsForType = () => {
    if (itemType === 'insight') {
      return (
        <div className='insightItemConfig__inputs'>
          <SettingsIconSelectInput
            filterOptions={integrationOptions}
            value={integration}
            marginTop='1.6rem'
            title={t('Integration')}
            onFilterChange={(val) => setIntegration(val)}
            emptyOptionPlaceholder={
              integrationOptions.length
                ? t('SelectIntegration')
                : t('NoOptions')
            }
          />
          {integration && baseMetricOptions.length > 0 ? (
            <>
              {metricsSelected.map((val, idx) => (
                <SettingsIconSelectInput
                  key={`metric-${idx}`}
                  filterOptions={getOptionsForMetricIndex(idx)}
                  value={val}
                  marginTop='1.6rem'
                  title={idx === 0 ? t('Metrics') : undefined}
                  onFilterChange={(v) => handleMetricChange(idx, v)}
                  emptyOptionPlaceholder={t('SelectMetric')}
                />
              ))}
            </>
          ) : null}
        </div>
      );
    }

    if (itemType === 'prompt') {
      return (
        <div className='insightItemConfig__inputs'>
          <SettingsIconSelectInput
            filterOptions={promptOptions}
            value={customPromptId}
            onFilterChange={(val) => setCustomPromptId(val)}
            emptyOptionPlaceholder={
              promptOptions.length ? t('SelectPrompt') : t('NoOptions')
            }
            title={t('CustomPrompt')}
            marginTop='1.5rem'
          />
        </div>
      );
    }

    if (itemType === 'graph') {
      return (
        <div className='insightItemConfig__inputs'>
          <SettingsIconSelectInput
            filterOptions={graphOptions}
            value={graphId}
            onFilterChange={(val) => setGraphId(val)}
            emptyOptionPlaceholder={
              graphOptions.length ? t('SelectGraph') : t('NoOptions')
            }
            title={t('Graph')}
            marginTop='1.5rem'
          />
        </div>
      );
    }

    return null;
  };

  return (
    <div className='insightItemConfig reportsGraphsForm'>
      <div className='insightItemConfig__title'>
        <div className='insightItemConfig__title--main'>
          <CustomSVGs svg={plusSignSVG} />
          <h3>{t('AddNotificationItem')}</h3>
        </div>
      </div>

      {/* Type picker */}
      <div className='insightItemConfig__inputs'>
        <SettingsIconSelectInput
          filterOptions={[
            { text: t('Insight'), value: 'insight', icon: chartBarSVG },
            { text: t('CustomPrompt'), value: 'prompt', icon: chatSquareSVG },
            { text: t('Graph'), value: 'graph', icon: trendLineSVG },
          ]}
          value={itemType}
          onFilterChange={(val) => setItemType(val as any)}
          title={t('ItemType')}
          marginTop='1.5rem'
        />
      </div>

      {renderInputsForType()}

      <div
        style={{ marginTop: '2.5rem' }}
        className='insightItemConfig__buttons'
      >
        <MainSquareButton
          value={t('Cancel')}
          type='button'
          buttonType='white'
          disabled={isSubmitting}
          onClick={() => togglePopupModal(false)}
        />
        <MainSquareButton
          value={isSubmitting ? t('Adding...') : t('AddItem')}
          type='button'
          disabled={isSaveDisabled}
          onClick={handleSave}
        />
      </div>
    </div>
  );
};

export default AddReportsNotifications;
